import { useState, useEffect } from 'react'
import { fmtTime } from './AoVivoSidebar'

type RecState = 'idle' | 'recording' | 'paused-rec' | 'uploading' | 'done'

interface AoVivoHeaderProps {
  reuniao: { titulo: string; hora_reuniao?: string | null; hora_fim?: string | null }
  seconds: number
  paused: boolean
  cdLabel?: string | null
  cdColor?: string
  recState: RecState
  recSecs?: number
  isRec?: boolean
  lastSaved?: string | null
  pendingCount?: number
  onPause: () => void
  onResume: () => void
  onEncerrar: () => void
  onCancel: () => void
  onToCapsule: () => void
  onToCompact: () => void
  mobileSidebar: boolean
  onToggleSidebar: () => void
  deviceCount?: number
  /** Outro aparelho gravando esta reunião agora (vem do Presence, não de broadcast). */
  outroGravando?: boolean
  isMeet?: boolean
}

export default function AoVivoHeader({
  reuniao, seconds, paused, cdLabel, cdColor, recState, recSecs,
  isRec, lastSaved, pendingCount = 0, onPause, onResume, onEncerrar, onCancel,
  onToCapsule, onToCompact,
  mobileSidebar, onToggleSidebar,
  deviceCount = 1, outroGravando, isMeet,
}: AoVivoHeaderProps) {
  // Flash verde quando um segmento é salvo
  const [savedFlash, setSavedFlash] = useState<boolean>(false)
  useEffect(() => {
    if (!lastSaved) return
    setSavedFlash(true)
    const t = setTimeout(() => setSavedFlash(false), 2000)
    return () => clearTimeout(t)
  }, [lastSaved])

  const canEnd = recState !== 'uploading'

  return (
    <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-3 border-b border-white/5 shrink-0">
      <button onClick={onCancel} title="Sair sem salvar" aria-label="Sair sem salvar"
        className="h-7 px-3 rounded-full flex items-center gap-1.5 bg-transparent border border-vermelho/30 cursor-pointer text-vermelho/60 hover:text-vermelho hover:border-vermelho/50 hover:bg-vermelho/8 transition-colors shrink-0">
        <span className="material-symbols-outlined text-sm">logout</span>
        <span className="text-[10px] font-semibold uppercase tracking-wide hidden sm:inline">Sair</span>
      </button>

      {/* Toggle sidebar — visível só no mobile */}
      <button
        onClick={onToggleSidebar}
        title={mobileSidebar ? 'Ocultar participantes' : 'Participantes'}
        aria-label="Alternar participantes"
        className={`md:hidden h-7 w-7 rounded-full flex items-center justify-center border cursor-pointer transition-colors shrink-0
          ${mobileSidebar
            ? 'bg-white/[0.06] border-white/15 text-[#f0f0f0]'
            : 'bg-transparent border-white/[0.07] text-[#555] hover:text-[#f0f0f0] hover:border-white/15'}`}
      >
        <span className="material-symbols-outlined text-[15px]">people</span>
      </button>

      {/* Minimizar para compact ou capsule */}
      <button onClick={onToCompact} title="Janela compacta" aria-label="Janela compacta"
        className="hidden sm:flex h-7 w-7 rounded-full items-center justify-center bg-transparent border border-white/[0.07] cursor-pointer text-[#555] hover:text-[#f0f0f0] hover:border-white/15 transition-colors shrink-0">
        <span className="material-symbols-outlined text-[15px]">pip</span>
      </button>
      <button onClick={onToCapsule} title="Minimizar para cápsula" aria-label="Minimizar para cápsula"
        className="hidden sm:flex h-7 w-7 rounded-full items-center justify-center bg-transparent border border-white/[0.07] cursor-pointer text-[#555] hover:text-[#f0f0f0] hover:border-white/15 transition-colors shrink-0">
        <span className="material-symbols-outlined text-[15px]">close_fullscreen</span>
      </button>

      <div className="flex-1 min-w-0">
        <p className="text-[10px] tracking-[0.09em] uppercase text-muted hidden sm:block">
          {reuniao.hora_reuniao?.slice(0,5) && `${reuniao.hora_reuniao.slice(0,5)}${reuniao.hora_fim ? ` → ${reuniao.hora_fim.slice(0,5)}` : ''} · `}
          Reunião ao vivo
        </p>
        <h2 className="text-base sm:text-xl font-semibold text-[#f0f0f0] truncate leading-tight">{reuniao.titulo}</h2>
      </div>

      <div className="flex items-center gap-2 sm:gap-3 shrink-0">
        {/* Devices conectados */}
        {deviceCount > 1 && (
          <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-hero/10 border border-hero/20" title={`${deviceCount} dispositivos conectados`}>
            {Array.from({ length: deviceCount }).map((_, i) => (
              <span key={i} className="w-1.5 h-1.5 rounded-full bg-hero shrink-0" />
            ))}
            <span className="text-[9px] text-hero font-medium ml-0.5">{deviceCount}</span>
          </div>
        )}
        {/* Outro aparelho gravando.
            A condição antiga exigia `recState !== 'recording'`, ou seja: o aviso
            era suprimido exatamente no caso que importa, o de dois aparelhos
            gravando ao mesmo tempo. Agora esse é o caso que grita. */}
        {outroGravando && (recState === 'recording' ? (
          <div role="alert"
            className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-[var(--color-vermelho)]/15 border border-[var(--color-vermelho)]/30"
            title="Dois aparelhos estão gravando esta reunião ao mesmo tempo. Pare um deles.">
            <span className="w-1.5 h-1.5 rounded-full animate-pulse shrink-0" style={{ background: 'var(--color-vermelho)' }} />
            <span className="text-[10px] font-bold text-[var(--color-vermelho)] whitespace-nowrap">2 gravações</span>
          </div>
        ) : (
          <div className="flex items-center gap-1" title="A gravação está rodando em outro aparelho">
            <span className="w-1.5 h-1.5 rounded-full animate-pulse shrink-0" style={{ background: 'var(--color-vermelho)' }} />
            <span className="text-[10px] text-[#999] whitespace-nowrap hidden sm:inline">Gravando em outro aparelho</span>
          </div>
        ))}
        {/* Countdown */}
        {cdLabel && (
          <span className="text-[11px] tabular-nums font-medium hidden sm:inline" style={{ color: cdColor }}>
            {cdLabel}
          </span>
        )}
        {/* Timer principal + indicador REC/Meet integrado */}
        <div className="flex items-center gap-2">
          {isMeet ? (
            <div className="flex items-center gap-1.5">
              <span className="material-symbols-outlined text-sm text-hero">video_call</span>
              <span className="text-[10px] font-semibold text-hero uppercase tracking-wide">Meet</span>
            </div>
          ) : (
            <>
              {recState === 'recording' && (
                <div className="flex items-center gap-1.5" aria-live="assertive">
                  <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: 'var(--color-vermelho)' }} />
                  <span className="text-[10px] font-semibold uppercase" style={{ color: 'var(--color-vermelho)' }}>REC</span>
                  {savedFlash && (
                    <span className="text-[9px] text-[var(--color-verde)] font-medium animate-pulse">salvo</span>
                  )}
                </div>
              )}
              {recState === 'paused-rec' && (
                <div className="flex items-center gap-1.5" aria-live="assertive">
                  <span className="w-2 h-2 rounded-full bg-[#444]" />
                  <span className="text-[10px] text-[#555] font-semibold uppercase">REC pausado</span>
                </div>
              )}
              {recState === 'uploading' && (
                <div className="flex items-center gap-1.5" aria-live="assertive">
                  <span className="material-symbols-outlined animate-spin text-[#888] text-sm">progress_activity</span>
                  <span className="text-[10px] text-[#888]">Salvando...</span>
                </div>
              )}
              {pendingCount > 0 && (
                <div className="flex items-center gap-1.5" aria-live="polite" title={`${pendingCount} segmento(s) aguardando upload`}>
                  <span className="material-symbols-outlined text-sm" style={{ color: 'var(--color-dourado)' }}>cloud_off</span>
                  <span className="text-[10px] font-medium" style={{ color: 'var(--color-dourado)' }}>{pendingCount} pendente{pendingCount > 1 ? 's' : ''}</span>
                </div>
              )}
            </>
          )}
          <span className="text-xl sm:text-2xl font-light text-[#f0f0f0] tabular-nums" aria-live="off" aria-label={`Tempo decorrido: ${fmtTime(seconds)}`}>{fmtTime(seconds)}</span>
        </div>
        {/* Pausa / Retomar — Ctrl+P */}
        <button onClick={paused ? onResume : onPause}
          title={paused ? 'Retomar (Ctrl+P)' : 'Pausar (Ctrl+P)'}
          className="h-8 px-3 sm:px-4 rounded-full text-[10px] font-semibold uppercase tracking-[0.06em] cursor-pointer border transition-colors"
          style={paused
            ? { color: '#090909', background: 'var(--color-hero)', borderColor: 'transparent' }
            : { color: '#666', background: 'transparent', borderColor: 'rgba(255,255,255,0.10)' }}>
          {paused ? '▶ Retomar' : '⏸ Pausa'}
        </button>
        {/* Encerrar — Ctrl+E */}
        <button onClick={onEncerrar} disabled={!canEnd}
          title={!canEnd ? 'Aguardando envio…' : 'Encerrar (Ctrl+E)'}
          className="h-8 px-3 sm:px-4 rounded-full text-[10px] font-semibold uppercase tracking-[0.06em] text-white border-none cursor-pointer transition-opacity"
          style={{ background: 'var(--color-vermelho)', opacity: canEnd ? 1 : 0.4, cursor: canEnd ? 'pointer' : 'not-allowed' }}>
          Encerrar
        </button>
      </div>
    </div>
  )
}

interface PauseOverlayProps {
  recState: RecState
  onResume: () => void
  isMeet?: boolean
}

export function PauseOverlay({ recState, onResume, isMeet }: PauseOverlayProps) {
  return (
    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4"
      style={{ background: 'rgba(17,17,17,0.92)', backdropFilter: 'blur(12px)' }}>
      <span className="material-symbols-outlined text-[44px]" style={{ color: '#333' }}>pause_circle</span>
      <div className="text-center">
        <p className="text-[15px] font-semibold text-[#888]">Sessão pausada</p>
        <p className="text-[12px] text-[#555] mt-1">
          {isMeet ? 'Cronômetro pausado.' : recState === 'paused-rec' ? 'Gravação de áudio suspensa — arquivo menor ao retomar.' : 'Cronômetro pausado.'}
        </p>
        <p className="text-[10px] text-[#333] mt-1">Ctrl+P para retomar</p>
      </div>
      <button onClick={onResume}
        className="h-9 px-6 rounded-full text-[12px] font-semibold cursor-pointer border-none"
        style={{ background: 'var(--color-hero)', color: '#090909' }}>
        ▶ Retomar sessão
      </button>
    </div>
  )
}
